import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { PLATFORM_BRAND, type BrandDef } from '@/lib/brand'
import { PUBLIC_SIGNUP_ENABLED } from '@/lib/signupFlag'
import { signInThrottled } from '@/lib/loginThrottle'
import { isNoMailboxAddress } from '@/lib/loginName'
import OAuthSignInButtons from './OAuthSignInButtons'

// Sign-in itself is signInDirect (src/lib/authDirect.ts), wrapped by
// signInThrottled (src/lib/loginThrottle.ts — shared with the admin app's
// login): a raw POST to /auth/v1/token that bypasses the supabase-js
// navigator.locks path, which hangs tenant-switching and AcceptInvite saves
// when banner / session-refresh queries fire on first paint. The session is
// written to localStorage in supabase-js v2's shape and we reload; supabase-js
// rehydrates with no lock.

// `brand` — the brand the page is drawn for. Every route mounts the page bare,
// so today it is always the platform brand (sign-in is pre-auth: there is no
// tenant yet to brand it by). It is the seam a white-label login would pass its
// own brand through, and what the tests drive.
export default function LoginPage({ brand = PLATFORM_BRAND }: { brand?: BrandDef }) {
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState<string | null>(null)
  const [loading,  setLoading]  = useState(false)

  // Forgot-password sub-flow. When `mode='forgot'` we hide the password
  // field and submit-button switches to "Send reset link" — Supabase Auth
  // sends a recovery email that lands on /accept-invite where the user
  // sets a new password.
  const [mode, setMode]     = useState<'login' | 'forgot'>('login')
  const [sent, setSent]     = useState(false)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (mode === 'forgot') {
      // A staff login name (docs/specs/no-mailbox-logins.md §3.6) has no
      // inbox: a recovery mail would go nowhere and "Check your email" would
      // be a lie. The admin is that person's reset path.
      if (isNoMailboxAddress(email)) {
        setLoading(false)
        setError('This login has no mailbox, so a reset link cannot be emailed. ' +
                 'Ask your admin for a reset link — they can make one from the Users or Members page in a few seconds.')
        return
      }
      const redirectTo = `${window.location.origin}/accept-invite`
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
      setLoading(false)
      if (error) { setError(error.message); return }
      setSent(true)
      return
    }

    const failure = await signInThrottled(email, password)
    if (failure) {
      setError(failure)
      setLoading(false)
      return
    }

    // Success — session is in localStorage. Reload so supabase-js
    // rehydrates from disk with no lock state, then the router lands on
    // the destination set by the authed session bootstrap.
    window.location.reload()
  }

  return (
    <div className="min-h-full flex flex-col items-center justify-center p-6 surface-sunk">
      <div className="w-full max-w-sm">
        {/* Logo area */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-green-600 mb-4">
            <span className="text-3xl font-black text-white">B</span>
          </div>
          <h1 className="text-2xl font-bold text-fg">{brand.appName}</h1>
          <p className="text-fg-muted mt-1">
            {mode === 'forgot' ? 'Reset your password' : 'Sign in to continue'}
          </p>
        </div>

        {sent ? (
          <div className="rounded-xl bg-emerald-900/40 border border-emerald-700 px-4 py-4 text-sm text-emerald-200 space-y-2">
            <p className="font-semibold">Check your email</p>
            <p className="text-emerald-300/80 text-xs">
              We've sent a reset link to <span className="font-mono">{email}</span>. The link is valid for one hour.
            </p>
            <button
              onClick={() => { setMode('login'); setSent(false); setEmail(''); setError(null) }}
              className="text-xs text-emerald-300 hover:text-emerald-200 underline">
              Back to sign-in
            </button>
          </div>
        ) : (
          <>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                className="field"
                placeholder="you@yourfarm.co.za"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <p className="text-xs text-fg-muted mt-1">Floor staff: use the login name on your card.</p>
            </div>

            {mode === 'login' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label mb-0" htmlFor="login-password">Password</label>
                  <button type="button"
                    onClick={() => { setMode('forgot'); setError(null) }}
                    className="text-xs text-blue-400 hover:text-blue-300">
                    Forgot password?
                  </button>
                </div>
                <input
                  id="login-password"
                  type="password"
                  className="field"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-red-900/50 border border-red-700 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading
                ? (mode === 'forgot' ? 'Sending…' : 'Signing in…')
                : (mode === 'forgot' ? 'Send reset link' : 'Sign In')}
            </button>

            {mode === 'forgot' && (
              <button type="button"
                onClick={() => { setMode('login'); setError(null) }}
                className="w-full text-xs text-fg-muted hover:text-fg">
                Back to sign-in
              </button>
            )}
          </form>
          {mode === 'login' && <OAuthSignInButtons onError={setError} />}
          </>
        )}
        {/* POPIA — make the policy reachable from the login screen so
            prospective users and operators can read it before creating
            an account. */}
        {PUBLIC_SIGNUP_ENABLED && (
          <p className="text-center text-xs text-fg-muted mt-6">
            New farm?{' '}
            <a href="/signup" className="text-blue-400 hover:text-blue-300">Request an account</a>
          </p>
        )}
        {/* Someone who reached the app before the marketing site: point them
            at what the platform does. A plain <a> on purpose — a followable
            link from the app's own domain is how search engines find the
            site, so no JS navigation and no `nofollow`. Gated on the brand's
            own field (src/lib/brand.ts): a brand without a marketing site
            renders nothing here. */}
        {brand.marketingUrl && (
          <p className="text-center text-xs text-fg-muted mt-4">
            New to {brand.appName}?{' '}
            <a href={brand.marketingUrl} target="_blank" rel="noopener"
              className="inline-flex items-center tap-target-y text-blue-400 hover:text-blue-300 underline underline-offset-2">
              See what it does<span className="sr-only"> (opens in a new tab)</span>
            </a>
          </p>
        )}
        <p className="text-center text-[11px] text-fg-muted mt-3">
          <a href="/terms" className="hover:text-fg-secondary">Terms of Service</a>
          <span className="mx-1.5">·</span>
          <a href="/privacy" className="hover:text-fg-secondary">Privacy Policy</a>
        </p>
      </div>
    </div>
  )
}
